import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Divider,
  Link,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Add,
  Delete,
  Refresh,
  Phone,
  DirectionsCar,
  Visibility,
  Description,
  Warning,
  CheckCircle,
  VpnKey,
  Edit,
  Save,
  Cancel,
  ThumbUp,
  DeleteForever,
  RestoreFromTrash,
  Download,
  History,
} from '@mui/icons-material';
import { driversAPI } from '../services/api';

// API 기본 URL (파일 다운로드용)
const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://54.180.91.91:3000';

const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0,2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0,2)}-${digits.slice(2,5)}-${digits.slice(5)}`;
    return `${digits.slice(0,2)}-${digits.slice(2,6)}-${digits.slice(6,10)}`;
  }
  if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  return `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7,11)}`;
};

function DriverList() {
  const [drivers, setDrivers] = useState([]);
  const [deletedDrivers, setDeletedDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0); // 0: 전체, 1: 승인대기, 2: 삭제됨
  const [approveWarningOpen, setApproveWarningOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveChecks, setApproveChecks] = useState({ docs: false, legal: false, identity: false });

  // 현재 로그인 사용자 역할
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'super_admin';

  // 신규 등록용
  const [newDriverData, setNewDriverData] = useState({
    name: '',
    phone: '',
    pin: '',
    vehicle_number: '',
    vehicle_type: '카고',
    vehicle_weight: '',
    bank_name: '',
    account_number: '',
    special_notes: '',
  });

  const vehicleTypes = ['카고', '탑차', '냉동차', '윙바디', '트레일러', '기타'];
  const WEIGHT_OPTIONS = [1, 1.4, 2.5, 3.5, 5, 8, 11, 14, 18, 25];
  const banks = ['국민은행', '신한은행', '우리은행', '하나은행', '농협', 'IBK기업은행', '카카오뱅크', '토스뱅크'];

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const response = await driversAPI.getAll();
      setDrivers(response.data.data || []);

      // 최고관리자면 삭제된 목록도 로드
      if (isSuperAdmin) {
        try {
          const deletedResponse = await driversAPI.getDeleted();
          setDeletedDrivers(deletedResponse.data.data || []);
        } catch (e) {
          console.log('삭제된 목록 로드 실패:', e);
        }
      }
    } catch (err) {
      console.error('운전자 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  // 상세보기 다이얼로그 (최신 데이터 로드)
  const handleOpenDetailDialog = async (driver) => {
    setSelectedDriver(driver);
    setEditFormData({ ...driver });
    setDetailEditMode(false);
    setDetailDialogOpen(true);
    try {
      const res = await driversAPI.getById(driver.driver_id);
      if (res.data.success) {
        const fresh = res.data.data.driver;
        setSelectedDriver(fresh);
        setEditFormData({ ...fresh });
      }
    } catch (err) {
      console.error('상세 조회 오류:', err);
    }
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedDriver(null);
    setDetailEditMode(false);
    setEditFormData({});
  };

  const handleToggleEditMode = () => {
    if (detailEditMode) {
      setEditFormData({ ...selectedDriver });
    }
    setDetailEditMode(!detailEditMode);
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDriver = async () => {
    try {
      setSaving(true);
      await driversAPI.update(selectedDriver.driver_id, editFormData);

      if (editFormData.rating !== selectedDriver.rating) {
        await driversAPI.updateRating(selectedDriver.driver_id, editFormData.rating);
      }

      alert('저장되었습니다.');
      setDetailEditMode(false);
      setSelectedDriver({ ...editFormData });
      loadDrivers();
    } catch (err) {
      alert(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 신규 등록
  const handleOpenNewDialog = () => {
    setNewDriverData({
      name: '', phone: '', pin: '', vehicle_number: '',
      vehicle_type: '카고', vehicle_weight: '', bank_name: '',
      account_number: '', special_notes: '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const handleCreateDriver = async () => {
    if (!newDriverData.name || !newDriverData.phone || !newDriverData.vehicle_number) {
      alert('이름, 전화번호, 차량번호는 필수입니다.');
      return;
    }
    if (!newDriverData.pin || newDriverData.pin.length !== 4) {
      alert('PIN 4자리를 입력하세요.');
      return;
    }
    try {
      await driversAPI.create(newDriverData);
      handleCloseDialog();
      loadDrivers();
    } catch (err) {
      alert(err.response?.data?.message || '등록에 실패했습니다.');
    }
  };

  // 승인 - 법적 주의사항 확인 후 처리
  const handleApprove = (driver) => {
    setApproveTarget(driver);
    setApproveChecks({ docs: false, legal: false, identity: false });
    setApproveWarningOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!approveChecks.docs || !approveChecks.legal || !approveChecks.identity) {
      alert('모든 항목을 확인하셔야 승인할 수 있습니다.');
      return;
    }
    try {
      await driversAPI.approve(approveTarget.driver_id);
      alert(`${approveTarget.name} 운전자가 승인되었습니다.`);
      setApproveWarningOpen(false);
      setApproveTarget(null);
      loadDrivers();
    } catch (err) {
      alert(err.response?.data?.message || '승인에 실패했습니다.');
    }
  };

  // 소프트 삭제
  const handleDelete = async (driver) => {
    if (!window.confirm(`${driver.name} 운전자를 삭제하시겠습니까?\n(최고관리자가 복원하거나 완전 삭제할 수 있습니다)`)) return;
    try {
      await driversAPI.delete(driver.driver_id);
      loadDrivers();
    } catch (err) {
      alert(err.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  // 완전 삭제 (최고관리자)
  const handlePermanentDelete = async (driver) => {
    if (!window.confirm(`[경고] ${driver.name} 운전자를 완전 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.\n감사 로그에만 기록이 남습니다.`)) return;
    if (!window.confirm('정말로 완전 삭제하시겠습니까? (최종 확인)')) return;
    try {
      await driversAPI.permanentDelete(driver.driver_id);
      alert('완전 삭제되었습니다. 감사 로그에 기록이 남아있습니다.');
      loadDrivers();
    } catch (err) {
      alert(err.response?.data?.message || '완전 삭제에 실패했습니다.');
    }
  };

  // 복원 (최고관리자)
  const handleRestore = async (driver) => {
    if (!window.confirm(`${driver.name} 운전자를 복원하시겠습니까?`)) return;
    try {
      await driversAPI.restore(driver.driver_id);
      alert('복원되었습니다. 비활성 상태이며 별도 승인이 필요합니다.');
      loadDrivers();
    } catch (err) {
      alert(err.response?.data?.message || '복원에 실패했습니다.');
    }
  };

  // 상태 토글
  const handleToggleActive = async (driver) => {
    try {
      await driversAPI.toggleActive(driver.driver_id);
      loadDrivers();
    } catch (err) {
      alert('상태 변경에 실패했습니다.');
    }
  };

  // PIN 변경
  const handleOpenPasswordDialog = (driver) => {
    setSelectedDriver(driver);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length !== 4) {
      alert('새 PIN 4자리를 입력하세요.');
      return;
    }
    try {
      await driversAPI.changePassword(selectedDriver.driver_id, newPassword);
      alert(`${selectedDriver.name} 운전자의 PIN이 변경되었습니다.`);
      setPasswordDialogOpen(false);
      setNewPassword('');
    } catch (err) {
      alert(err.response?.data?.message || 'PIN 변경에 실패했습니다.');
    }
  };

  // 감사 로그
  const handleOpenAuditLog = async (driverId) => {
    try {
      const params = driverId ? { driver_id: driverId } : {};
      const response = await driversAPI.getAuditLog(params);
      setAuditLogs(response.data.data || []);
      setAuditDialogOpen(true);
    } catch (err) {
      alert('로그 조회에 실패했습니다.');
    }
  };

  const handleDownloadAuditLog = async () => {
    try {
      const response = await driversAPI.downloadAuditLog();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `driver_audit_log_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('다운로드에 실패했습니다.');
    }
  };

  const handleRatingChange = (rating) => {
    setEditFormData(prev => ({ ...prev, rating }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const getFileUrl = (filename) => {
    if (!filename) return null;
    return `${API_BASE_URL}/uploads/drivers/${filename}`;
  };

  // 필터링
  const pendingDrivers = drivers.filter(d => !d.is_active && !d.approved_at);
  const activeDriversList = drivers.filter(d => d.is_active);
  const inactiveDrivers = drivers.filter(d => !d.is_active && d.approved_at);

  const getFilteredDrivers = () => {
    switch (tabValue) {
      case 1: return pendingDrivers; // 승인 대기
      case 2: return deletedDrivers; // 삭제됨 (최고관리자)
      default: return drivers; // 전체
    }
  };

  const totalDrivers = drivers.length;
  const activeCount = activeDriversList.length;
  const pendingCount = pendingDrivers.length;
  const deletedCount = deletedDrivers.length;

  const actionLabels = {
    registered: '등록',
    approved: '승인',
    rejected: '반려',
    deactivated: '비활성화',
    activated: '활성화',
    deleted: '삭제',
    hard_deleted: '완전삭제',
    updated: '수정',
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="bold">운전자 관리</Typography>
        <Box>
          {isSuperAdmin && (
            <Tooltip title="감사 로그">
              <Button startIcon={<History />} onClick={() => handleOpenAuditLog()} sx={{ mr: 1 }}>
                작업 로그
              </Button>
            </Tooltip>
          )}
          <Button startIcon={<Refresh />} onClick={loadDrivers} sx={{ mr: 1 }}>새로고침</Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenNewDialog}>
            운전자 등록
          </Button>
        </Box>
      </Box>

      {/* 탭 */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`전체 (${totalDrivers})`} />
        <Tab
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              승인 대기
              {pendingCount > 0 && (
                <Chip label={pendingCount} size="small" color="warning" sx={{ height: 20, fontSize: 12 }} />
              )}
            </Box>
          }
        />
        {isSuperAdmin && (
          <Tab label={`삭제됨 (${deletedCount})`} />
        )}
      </Tabs>

      {/* 승인 대기 알림 */}
      {pendingCount > 0 && tabValue !== 1 && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button size="small" onClick={() => setTabValue(1)}>확인하기</Button>
        }>
          승인 대기 중인 운전자가 {pendingCount}명 있습니다.
        </Alert>
      )}

      {/* 통계 */}
      <Typography variant="body2" color="textSecondary" mb={2}>
        총 {totalDrivers}명 | 승인: {activeCount}명 | 승인대기: {pendingCount}명 | 비활성: {inactiveDrivers.length}명
        {isSuperAdmin && ` | 삭제됨: ${deletedCount}명`}
      </Typography>

      {/* 테이블 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>이름</TableCell>
              <TableCell>전화번호</TableCell>
              <TableCell>차량번호</TableCell>
              <TableCell>차종</TableCell>
              <TableCell align="center">승인</TableCell>
              <TableCell align="center">관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : getFilteredDrivers().length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  {tabValue === 1 ? '승인 대기 중인 운전자가 없습니다.' :
                   tabValue === 2 ? '삭제된 운전자가 없습니다.' :
                   '등록된 운전자가 없습니다.'}
                </TableCell>
              </TableRow>
            ) : (
              getFilteredDrivers().map((driver) => (
                <TableRow key={driver.driver_id} hover
                  sx={tabValue === 2 ? { backgroundColor: '#fff3e0' } : {}}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DirectionsCar fontSize="small" color="action" />
                      {driver.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Phone fontSize="small" color="action" />
                      {formatPhone(driver.phone)}
                    </Box>
                  </TableCell>
                  <TableCell>{driver.vehicle_number}</TableCell>
                  <TableCell>{driver.vehicle_type}</TableCell>
                  <TableCell align="center">
                    {tabValue === 2 ? (
                      <Chip label="삭제됨" size="small" color="error" variant="outlined" />
                    ) : !driver.is_active && !driver.approved_at ? (
                      <Tooltip title="클릭하여 승인">
                        <Chip label="승인대기" size="small" color="warning"
                          onClick={() => handleApprove(driver)} sx={{ cursor: 'pointer' }} />
                      </Tooltip>
                    ) : (
                      <Switch
                        checked={driver.is_active ? true : false}
                        onChange={() => handleToggleActive(driver)}
                        color="primary"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {tabValue === 2 ? (
                      <Box>
                        <Tooltip title="복원">
                          <IconButton size="small" color="success" onClick={() => handleRestore(driver)}>
                            <RestoreFromTrash fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="완전 삭제">
                          <IconButton size="small" color="error" onClick={() => handlePermanentDelete(driver)}>
                            <DeleteForever fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="상세보기">
                          <IconButton size="small" color="info" onClick={() => handleOpenDetailDialog(driver)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box>
                        <Tooltip title="상세보기/수정">
                          <IconButton size="small" color="info" onClick={() => handleOpenDetailDialog(driver)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton size="small" color="error" onClick={() => handleDelete(driver)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 신규 등록 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>운전자 등록</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="이름 *" value={newDriverData.name}
                onChange={(e) => setNewDriverData({ ...newDriverData, name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="전화번호 *" value={newDriverData.phone}
                onChange={(e) => setNewDriverData({ ...newDriverData, phone: formatPhone(e.target.value) })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="PIN (4자리) *" type="password" value={newDriverData.pin}
                onChange={(e) => setNewDriverData({ ...newDriverData, pin: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })}
                inputProps={{ maxLength: 4 }} placeholder="숫자 4자리" helperText="앱 로그인에 사용됩니다" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="차량번호 *" value={newDriverData.vehicle_number}
                onChange={(e) => setNewDriverData({ ...newDriverData, vehicle_number: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>차종</InputLabel>
                <Select value={newDriverData.vehicle_type} label="차종"
                  onChange={(e) => setNewDriverData({ ...newDriverData, vehicle_type: e.target.value })}>
                  {vehicleTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>적재중량 (톤)</InputLabel>
                <Select value={newDriverData.vehicle_weight} label="적재중량 (톤)"
                  onChange={(e) => setNewDriverData({ ...newDriverData, vehicle_weight: e.target.value })}>
                  <MenuItem value="">선택</MenuItem>
                  {WEIGHT_OPTIONS.map(w => <MenuItem key={w} value={w}>{w}톤</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>은행</InputLabel>
                <Select value={newDriverData.bank_name} label="은행"
                  onChange={(e) => setNewDriverData({ ...newDriverData, bank_name: e.target.value })}>
                  <MenuItem value="">선택</MenuItem>
                  {banks.map(bank => <MenuItem key={bank} value={bank}>{bank}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="계좌번호" value={newDriverData.account_number}
                onChange={(e) => setNewDriverData({ ...newDriverData, account_number: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="특이사항" multiline rows={2} value={newDriverData.special_notes}
                onChange={(e) => setNewDriverData({ ...newDriverData, special_notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button variant="contained" onClick={handleCreateDriver}>등록</Button>
        </DialogActions>
      </Dialog>

      {/* 상세보기/수정 다이얼로그 */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <DirectionsCar />
              {selectedDriver?.name} 상세 정보
              {selectedDriver?.is_deleted ? (
                <Chip label="삭제됨" color="error" size="small" variant="outlined" />
              ) : !selectedDriver?.is_active && !selectedDriver?.approved_at ? (
                <Chip label="승인대기" color="warning" size="small" />
              ) : selectedDriver?.is_active ? (
                <Chip label="승인" color="success" size="small" />
              ) : (
                <Chip label="비활성" color="default" size="small" />
              )}
            </Box>
            <Box>
              {!selectedDriver?.is_deleted && !selectedDriver?.is_active && !selectedDriver?.approved_at && (
                <Button startIcon={<ThumbUp />} onClick={() => { handleApprove(selectedDriver); handleCloseDetailDialog(); }}
                  variant="contained" color="success" size="small" sx={{ mr: 1 }}>
                  승인
                </Button>
              )}
              {!detailEditMode ? (
                <Button startIcon={<Edit />} onClick={handleToggleEditMode} variant="outlined" size="small">
                  수정
                </Button>
              ) : (
                <Box display="inline">
                  <Button startIcon={<Cancel />} onClick={handleToggleEditMode} size="small" sx={{ mr: 1 }}>취소</Button>
                  <Button startIcon={<Save />} onClick={handleSaveDriver} variant="contained" size="small" disabled={saving}>
                    {saving ? '저장 중...' : '저장'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDriver && (
            <Box>
              {/* 기본 정보 */}
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>기본 정보</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">이름</Typography>
                  {detailEditMode ? (
                    <TextField fullWidth size="small" value={editFormData.name || ''}
                      onChange={(e) => handleFormChange('name', e.target.value)} />
                  ) : (
                    <Typography variant="body1">{selectedDriver.name}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">전화번호</Typography>
                  {detailEditMode ? (
                    <TextField fullWidth size="small" value={editFormData.phone || ''}
                      onChange={(e) => handleFormChange('phone', formatPhone(e.target.value))} />
                  ) : (
                    <Typography variant="body1">{formatPhone(selectedDriver.phone)}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">주민등록번호</Typography>
                  <Typography variant="body1">{selectedDriver.resident_number || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">주소</Typography>
                  {detailEditMode ? (
                    <TextField fullWidth size="small" value={editFormData.address || ''}
                      onChange={(e) => handleFormChange('address', e.target.value)} />
                  ) : (
                    <Typography variant="body1">{selectedDriver.address || '-'}</Typography>
                  )}
                </Grid>
              </Grid>

              {/* 사업자 정보 */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>사업자 정보</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">사업자번호</Typography>
                  <Typography variant="body1">{selectedDriver.business_number || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">사업자등록증</Typography>
                  {selectedDriver.business_file ? (
                    <Link href={getFileUrl(selectedDriver.business_file)} target="_blank" rel="noopener">
                      <Button size="small" startIcon={<Description />} variant="outlined">파일 보기</Button>
                    </Link>
                  ) : (
                    <Typography variant="body1" color="textSecondary">미등록</Typography>
                  )}
                </Grid>
              </Grid>

              {/* 차량 정보 */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>차량 정보</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">차량번호</Typography>
                  {detailEditMode ? (
                    <TextField fullWidth size="small" value={editFormData.vehicle_number || ''}
                      onChange={(e) => handleFormChange('vehicle_number', e.target.value)} />
                  ) : (
                    <Typography variant="body1">{selectedDriver.vehicle_number}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">차종</Typography>
                  {detailEditMode ? (
                    <FormControl fullWidth size="small">
                      <Select value={editFormData.vehicle_type || ''}
                        onChange={(e) => handleFormChange('vehicle_type', e.target.value)}>
                        {vehicleTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{selectedDriver.vehicle_type}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">적재중량</Typography>
                  {detailEditMode ? (
                    <FormControl fullWidth size="small">
                      <Select value={editFormData.weight_capacity || editFormData.vehicle_weight || ''}
                        onChange={(e) => handleFormChange('weight_capacity', e.target.value)}>
                        <MenuItem value="">선택</MenuItem>
                        {WEIGHT_OPTIONS.map(w => <MenuItem key={w} value={w}>{w}톤</MenuItem>)}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{(selectedDriver.weight_capacity || selectedDriver.vehicle_weight) ? `${selectedDriver.weight_capacity || selectedDriver.vehicle_weight}톤` : '-'}</Typography>
                  )}
                </Grid>
              </Grid>

              {/* 서류 정보 */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>서류 정보</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">화물운송 종사자격증</Typography>
                  {selectedDriver.cargo_certificate_file ? (
                    <Link href={getFileUrl(selectedDriver.cargo_certificate_file)} target="_blank" rel="noopener">
                      <Button size="small" startIcon={<Description />} variant="outlined">파일 보기</Button>
                    </Link>
                  ) : (
                    <Typography variant="body1" color="textSecondary">미등록</Typography>
                  )}
                </Grid>
              </Grid>

              {/* 정산 정보 */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>정산 정보</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">은행</Typography>
                  {detailEditMode ? (
                    <FormControl fullWidth size="small">
                      <Select value={editFormData.bank_name || ''}
                        onChange={(e) => handleFormChange('bank_name', e.target.value)}>
                        <MenuItem value="">선택</MenuItem>
                        {banks.map(bank => <MenuItem key={bank} value={bank}>{bank}</MenuItem>)}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{selectedDriver.bank_name || '-'}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">계좌번호</Typography>
                  {detailEditMode ? (
                    <TextField fullWidth size="small" value={editFormData.account_number || ''}
                      onChange={(e) => handleFormChange('account_number', e.target.value)} />
                  ) : (
                    <Typography variant="body1">{selectedDriver.account_number || '-'}</Typography>
                  )}
                </Grid>
              </Grid>

              {/* 기타 정보 */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>기타 정보</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">가입일</Typography>
                  <Typography variant="body1">{formatDate(selectedDriver.created_at)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">승인일</Typography>
                  <Typography variant="body1">{formatDate(selectedDriver.approved_at)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">개인정보동의</Typography>
                  {selectedDriver.privacy_agreed ? (
                    <Chip label="동의함" size="small" color="success" />
                  ) : (
                    <Chip label="미동의" size="small" color="default" />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">특이사항</Typography>
                  {detailEditMode ? (
                    <TextField fullWidth size="small" multiline rows={2} value={editFormData.special_notes || ''}
                      onChange={(e) => handleFormChange('special_notes', e.target.value)} />
                  ) : (
                    <Typography variant="body1">{selectedDriver.special_notes || '-'}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">별점</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star}
                        style={{
                          color: star <= (detailEditMode ? editFormData.rating : selectedDriver.rating) || 0 ? '#FFD700' : '#ddd',
                          fontSize: '24px',
                          cursor: detailEditMode ? 'pointer' : 'default'
                        }}
                        onClick={() => detailEditMode && handleRatingChange(star)}>
                        ★
                      </span>
                    ))}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      ({(detailEditMode ? editFormData.rating : selectedDriver.rating) || 0}점)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {isSuperAdmin && selectedDriver && (
            <Button startIcon={<History />} onClick={() => { handleCloseDetailDialog(); handleOpenAuditLog(selectedDriver.driver_id); }}
              size="small">
              작업 이력
            </Button>
          )}
          <Button onClick={handleCloseDetailDialog}>닫기</Button>
          <Button variant="outlined" color="warning" startIcon={<VpnKey />}
            onClick={() => { handleCloseDetailDialog(); handleOpenPasswordDialog(selectedDriver); }}>
            PIN 변경
          </Button>
        </DialogActions>
      </Dialog>

      {/* PIN 변경 다이얼로그 */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VpnKey color="warning" />
            PIN 변경
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {selectedDriver?.name} ({selectedDriver?.phone}) 운전자의 새 PIN을 입력하세요.
          </Typography>
          <TextField fullWidth label="새 PIN (4자리)" type="password" value={newPassword}
            onChange={(e) => setNewPassword(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
            inputProps={{ maxLength: 4 }} placeholder="숫자 4자리" helperText="숫자 4자리를 입력하세요" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>취소</Button>
          <Button variant="contained" color="warning" onClick={handleChangePassword}>PIN 변경</Button>
        </DialogActions>
      </Dialog>

      {/* 감사 로그 다이얼로그 */}
      <Dialog open={auditDialogOpen} onClose={() => setAuditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <History />
              작업 로그
            </Box>
            <Button startIcon={<Download />} onClick={handleDownloadAuditLog} size="small" variant="outlined">
              CSV 다운로드
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>일시</TableCell>
                  <TableCell>운전자ID</TableCell>
                  <TableCell>작업</TableCell>
                  <TableCell>수행자</TableCell>
                  <TableCell>메모</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>로그가 없습니다.</TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.log_id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.created_at)}</TableCell>
                      <TableCell>{log.driver_id}</TableCell>
                      <TableCell>
                        <Chip
                          label={actionLabels[log.action] || log.action}
                          size="small"
                          color={
                            log.action === 'approved' ? 'success' :
                            log.action === 'deleted' || log.action === 'hard_deleted' ? 'error' :
                            log.action === 'registered' ? 'info' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{log.performer_name || '-'}</TableCell>
                      <TableCell>{log.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* ===== 운전자 승인 법적 주의사항 다이얼로그 ===== */}
      <Dialog open={approveWarningOpen} onClose={() => setApproveWarningOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#fff8e1', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          운전자 승인 전 필수 확인사항
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
            <strong>법적 주의사항</strong><br />
            운전자 승인 시 화물운송 계약 관계가 성립되며, 허위·위변조 서류 확인 소홀에 대한 법적 책임이 업체에 귀속될 수 있습니다.
            아래 항목을 반드시 확인 후 승인하시기 바랍니다.
          </Alert>

          {approveTarget && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">승인 대상</Typography>
              <Typography variant="h6">{approveTarget.name}</Typography>
              <Typography variant="body2">전화번호: {approveTarget.phone}</Typography>
              <Typography variant="body2">차량번호: {approveTarget.vehicle_number} ({approveTarget.vehicle_type})</Typography>
            </Box>
          )}

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            다음 항목을 확인하셨습니까?
          </Typography>

          {[
            { key: 'docs', label: '화물운송종사자격증 및 사업자등록증 등 제출 서류를 검토하였습니다.' },
            { key: 'identity', label: '운전자의 신원 및 차량 정보가 서류와 일치함을 확인하였습니다.' },
            { key: 'legal', label: '허위 서류 제출 시 계약 해지 및 법적 책임이 발생함을 운전자에게 고지하였습니다.' },
          ].map(item => (
            <Box
              key={item.key}
              sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1,
                    cursor: 'pointer', backgroundColor: approveChecks[item.key] ? '#e8f5e9' : '#fff',
                    borderColor: approveChecks[item.key] ? '#4CAF50' : '#e0e0e0' }}
              onClick={() => setApproveChecks(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
            >
              {approveChecks[item.key]
                ? <CheckCircle sx={{ mr: 1, mt: 0.2, color: '#4CAF50', flexShrink: 0 }} />
                : <Warning sx={{ mr: 1, mt: 0.2, color: '#bbb', flexShrink: 0 }} />}
              <Typography variant="body2">{item.label}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveWarningOpen(false)}>취소</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveConfirm}
            disabled={!approveChecks.docs || !approveChecks.legal || !approveChecks.identity}
            startIcon={<ThumbUp />}
          >
            확인 후 승인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DriverList;
